import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  extractBase64FromDataUrl,
  usePatientSupportingDocumentsService,
  type ListedSupportingDocument,
} from "src-next/modules/patient-supporting-documents";
import {
  PATIENT_SUPPORTING_DOCUMENT_TYPES,
  PatientSupportingDocumentRecord,
  PatientSupportingDocumentsStore,
  PatientSupportingDocumentTypeId,
  getPatientSupportingDocumentType,
} from "./types";

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function listedItemToRecord(
  item: ListedSupportingDocument,
): PatientSupportingDocumentRecord {
  return {
    typeId: item.typeId,
    fileName: item.fileName,
    description: item.description,
    mimeType: item.mimeType,
    dataUrl: item.dataUrl,
    binaryUrl: item.binaryUrl,
    notes: item.notes,
    updatedAt: item.updatedAt,
    sentAt: item.sentAt ?? item.updatedAt,
    removedAt: item.removedAt,
    documentReferenceId: item.documentReferenceId,
    previousIdentifier: item.previousIdentifier,
    docStatus: item.docStatus,
  };
}

function listedToStore(
  listed: ListedSupportingDocument[],
): PatientSupportingDocumentsStore {
  const next: PatientSupportingDocumentsStore = {};
  for (const item of listed) {
    const record = listedItemToRecord(item);
    // Agrupa sem sobrescrever para preservar todos os documentos da categoria.
    next[item.typeId] = [...(next[item.typeId] ?? []), record];
  }

  for (const type of PATIENT_SUPPORTING_DOCUMENT_TYPES) {
    next[type.id]?.sort(
      (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
    );
  }
  return next;
}

export interface SavePatientSupportingDocumentInput {
  typeId: PatientSupportingDocumentTypeId;
  file?: File | null;
  notes: string;
  description?: string;
  existingDataUrl?: string;
  existingFileName?: string;
  existingMimeType?: string;
  /** Presente somente quando uma versão específica deve ser substituída. */
  existingDocument?: PatientSupportingDocumentRecord;
}

/**
 * Persistência = POST MHD; hidratação = Patient Summary (só 4 categories).
 * Remoção lógica = PATCH de docStatus para preservar auditoria no FHIR.
 */
export function usePatientSupportingDocuments(userId?: string) {
  const [documents, setDocuments] = useState<PatientSupportingDocumentsStore>(
    {},
  );
  const [cancelledDocuments, setCancelledDocuments] =
    useState<PatientSupportingDocumentsStore>({});
  const [isHydrated, setIsHydrated] = useState(false);
  const {
    postDocumentByType,
    cancelDocument,
    isPosting,
    isListing,
    listDocuments,
  } = usePatientSupportingDocumentsService();

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (!userId?.trim()) {
        setDocuments({});
        setCancelledDocuments({});
        setIsHydrated(true);
        return;
      }

      setIsHydrated(false);
      try {
        const listed = await listDocuments(userId.trim());
        if (!cancelled) {
          setDocuments(listedToStore(listed.active));
          setCancelledDocuments(listedToStore(listed.cancelled));
        }
      } catch (err) {
        if (!cancelled) {
          setDocuments({});
          setCancelledDocuments({});
          const message =
            err instanceof Error
              ? err.message
              : "Não foi possível carregar os documentos.";
          toast.error(message);
        }
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    }

    void hydrate();
    return () => {
      cancelled = true;
    };
    // Só userId: listDocuments estável após mapError em nível de módulo;
    // não incluir listDocuments evita loop se a identidade mudar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const uploadedCount = useMemo(
    () =>
      PATIENT_SUPPORTING_DOCUMENT_TYPES.filter(
        (type) => (documents[type.id]?.length ?? 0) > 0,
      ).length,
    [documents],
  );

  const totalCount = PATIENT_SUPPORTING_DOCUMENT_TYPES.length;

  const isDocumentsComplete = useMemo(
    () => uploadedCount === totalCount,
    [uploadedCount, totalCount],
  );

  const saveDocument = useCallback(
    async (input: SavePatientSupportingDocumentInput) => {
      if (!userId?.trim()) {
        toast.error("Paciente não identificado. Faça login novamente.");
        return false;
      }

      const config = getPatientSupportingDocumentType(input.typeId);
      const documentBeingReplaced = input.existingDocument;
      let dataUrl =
        documentBeingReplaced?.dataUrl ?? input.existingDataUrl ?? "";
      let fileName =
        documentBeingReplaced?.fileName ?? input.existingFileName ?? "";
      let mimeType =
        documentBeingReplaced?.mimeType ?? input.existingMimeType ?? "";

      if (input.file) {
        const sizeMb = input.file.size / (1024 * 1024);
        if (sizeMb > config.maxSizeMb) {
          toast.error(
            `O arquivo excede o limite de ${config.maxSizeMb} MB para este documento.`,
          );
          return false;
        }
        dataUrl = await fileToDataUrl(input.file);
        fileName = input.file.name;
        mimeType = input.file.type || "application/octet-stream";
      }

      if (!dataUrl) {
        toast.error("Selecione um arquivo para continuar.");
        return false;
      }

      const base64 = extractBase64FromDataUrl(dataUrl);
      if (!base64) {
        toast.error("Arquivo inválido. Tente novamente.");
        return false;
      }

      try {
        // Adições não recebem previousIdentifier e, portanto, acumulam documentos.
        // Somente a edição explícita de um item cria o relatesTo "replaces".
        const { documentReferenceId } = await postDocumentByType({
          patientId: userId.trim(),
          typeId: input.typeId,
          contentType: mimeType,
          base64,
          description: input.description?.trim() || undefined,
          previousIdentifier: documentBeingReplaced?.previousIdentifier,
        });

        const record: PatientSupportingDocumentRecord = {
          typeId: input.typeId,
          fileName,
          description: input.description?.trim() || undefined,
          mimeType,
          dataUrl,
          notes: input.notes.trim(),
          updatedAt: new Date().toISOString(),
          sentAt: new Date().toISOString(),
          documentReferenceId,
          // Identifier do novo documento (padrão do builder: urn:uuid:<uuid>),
          // para permitir nova substituição na mesma sessão sem re-listar.
          previousIdentifier: documentReferenceId
            ? {
                system: "urn:ietf:rfc:3986",
                value: `urn:uuid:${documentReferenceId}`,
              }
            : undefined,
        };

        setDocuments((prev) => {
          const currentDocuments = prev[input.typeId] ?? [];
          const nextDocuments = documentBeingReplaced
            ? currentDocuments.map((item) =>
                item.documentReferenceId ===
                  documentBeingReplaced.documentReferenceId &&
                item.updatedAt === documentBeingReplaced.updatedAt
                  ? record
                  : item,
              )
            : [record, ...currentDocuments];

          return {
            ...prev,
            [input.typeId]: nextDocuments,
          };
        });
        toast.success(
          documentBeingReplaced
            ? "Documento substituído com sucesso."
            : "Documento salvo com sucesso.",
        );
        return true;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Não foi possível enviar o documento. Tente novamente.";
        toast.error(message);
        return false;
      }
    },
    [userId, postDocumentByType],
  );

  /**
   * Remoção lógica: cancela no FHIR e só então atualiza a UI.
   * DELETE físico de DocumentReference permanece fora do escopo.
   */
  const removeDocument = useCallback(
    async (
      typeId: PatientSupportingDocumentTypeId,
      document: PatientSupportingDocumentRecord,
    ) => {
      if (!document.documentReferenceId) {
        toast.error("Documento não identificado para remoção.");
        return false;
      }

      try {
        // O recurso permanece no FHIR para auditoria, marcado como cancelado.
        await cancelDocument(document.documentReferenceId);
      } catch {
        toast.error("Não foi possível remover o documento. Tente novamente.");
        return false;
      }

      const cancelledRecord: PatientSupportingDocumentRecord = {
        ...document,
        docStatus: "cancelled",
        // Preserva envio; removedAt marca o instante da remoção nesta sessão.
        sentAt: document.sentAt || document.updatedAt,
        removedAt: new Date().toISOString(),
      };

      setDocuments((prev) => {
        const currentDocuments = prev[typeId] ?? [];
        return {
          ...prev,
          [typeId]: currentDocuments.filter(
            (item) =>
              item.documentReferenceId !== document.documentReferenceId ||
              item.updatedAt !== document.updatedAt,
          ),
        };
      });

      // Empurra para o histórico local sem re-fetch obrigatório.
      setCancelledDocuments((prev) => ({
        ...prev,
        [typeId]: [cancelledRecord, ...(prev[typeId] ?? [])],
      }));

      toast.success("Documento removido desta área.");
      return true;
    },
    [cancelDocument],
  );

  const getDocument = useCallback(
    // Compatibilidade: consumidores antigos continuam recebendo o mais recente.
    (typeId: PatientSupportingDocumentTypeId) => documents[typeId]?.[0],
    [documents],
  );

  const getCancelledDocuments = useCallback(
    (typeId: PatientSupportingDocumentTypeId) =>
      cancelledDocuments[typeId] ?? [],
    [cancelledDocuments],
  );

  return {
    documents,
    cancelledDocuments,
    getCancelledDocuments,
    isHydrated,
    isPosting,
    isListing,
    uploadedCount,
    totalCount,
    isDocumentsComplete,
    saveDocument,
    removeDocument,
    getDocument,
  };
}
